import React, { useState, useEffect, useCallback } from 'react';
import { billingAPI } from '../../api/client';
import { DodoPayments } from 'dodopayments-checkout';
import {
    trackPricingBuyClick,
    trackCheckoutOpened,
    trackCheckoutSuccess,
    trackCheckoutError,
    trackPricingModalClosed,
} from '../../utils/analytics';

const PRICING_TIERS = [
    { id: 'pack_starter', name: 'Starter Pack', price: 4.99, credits: 5, tag: null },
    { id: 'pack_builder', name: 'Builder Pack', price: 19.99, credits: 20, tag: null },
    { id: 'pack_pro', name: 'Pro Pack', price: 49.99, credits: 50, tag: 'Best Value' },
    { id: 'pack_team', name: 'Team Pack', price: 199.99, credits: 200, tag: null },
];

const PricingModal = ({ isOpen, onClose, onRefreshCredits }) => {
    const [loadingTier, setLoadingTier] = useState(null);
    const [error, setError] = useState('');
    const [overlayActive, setOverlayActive] = useState(false);

    // ── SDK Initialization (overlay mode) ────────────────────────────────────
    useEffect(() => {
        if (!isOpen) return;

        DodoPayments.Initialize({
            mode: process.env.REACT_APP_DODO_MODE === 'live' ? 'live' : 'test',
            displayType: 'overlay',
            onEvent: (event) => {
                switch (event.event_type) {
                    case 'checkout.status': {
                        const status = event.data?.message?.status;
                        if (status === 'succeeded') {
                            trackCheckoutSuccess('unknown', 'unknown', 0);
                            setOverlayActive(false);
                            onRefreshCredits();
                            onClose();
                        }
                        break;
                    }

                    case 'checkout.redirect_requested': {
                        const redirectTo = event.data?.message?.redirect_to;
                        if (redirectTo) window.location.href = redirectTo;
                        break;
                    }

                    case 'checkout.error':
                        console.error('Checkout error:', event.data?.message);
                        trackCheckoutError('unknown', event.data?.message || 'checkout_error');
                        setError('An error occurred during checkout. Please try again.');
                        break;

                    default:
                        break;
                }
            },
        });

        return () => {
            DodoPayments.Checkout.close();
        };
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Tier selection → create session → open overlay ───────────────────────
    const handleSelectTier = useCallback(async (tier) => {
        if (loadingTier) return;
        setError('');
        setLoadingTier(tier.id);
        trackPricingBuyClick(tier.id, tier.name, tier.price, tier.credits, 'app_modal');

        try {
            const response = await billingAPI.createCheckoutSession(tier.id);
            if (response.data?.checkoutUrl) {
                // Open Dodo overlay checkout
                DodoPayments.Checkout.open({
                    checkoutUrl: response.data.checkoutUrl,
                    options: {
                        showTimer: true,
                        showSecurityBadge: true,
                        manualRedirect: true,
                    },
                });
                trackCheckoutOpened(tier.id, tier.name, tier.price);
                // Hide our modal but keep component mounted so SDK stays alive
                setOverlayActive(true);
            } else {
                throw new Error('No checkoutUrl returned');
            }
        } catch (err) {
            console.error('Checkout session error:', err);
            trackCheckoutError(tier.id, err.message || 'session_creation_failed');
            setError('Failed to start checkout. Please try again.');
        } finally {
            setLoadingTier(null);
        }
    }, [loadingTier]);

    const handleClose = useCallback(() => {
        DodoPayments.Checkout.close();
        trackPricingModalClosed();
        setError('');
        setLoadingTier(null);
        setOverlayActive(false);
        onClose();
    }, [onClose]);

    if (!isOpen || overlayActive) return null;

    return (
        <>
            <style>{`
                .pm-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.7);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 16px;
                    backdrop-filter: blur(4px);
                }

                .pm-modal {
                    background: linear-gradient(145deg, #0f0f23 0%, #1a1a3e 100%);
                    border: 1px solid rgba(99, 102, 241, 0.25);
                    border-radius: 20px;
                    width: 100%;
                    max-width: 460px;
                    max-height: 90vh;
                    overflow: hidden;
                    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.05);
                    display: flex;
                    flex-direction: column;
                }

                .pm-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 20px 28px;
                    border-bottom: 1px solid rgba(99, 102, 241, 0.15);
                    flex-shrink: 0;
                }

                .pm-header-title {
                    font-size: 20px;
                    font-weight: 700;
                    color: #fff;
                }

                .pm-header-sub {
                    font-size: 13px;
                    color: rgba(255,255,255,0.5);
                    margin-top: 2px;
                }

                .pm-close-btn {
                    background: rgba(255,255,255,0.08);
                    border: 1px solid rgba(255,255,255,0.12);
                    color: rgba(255,255,255,0.6);
                    width: 34px;
                    height: 34px;
                    border-radius: 8px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    transition: all 0.2s;
                }
                .pm-close-btn:hover { background: rgba(255,255,255,0.14); color: white; }

                .pm-body {
                    overflow-y: auto;
                    padding: 20px 16px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                .pm-tier-card {
                    position: relative;
                    border: 1.5px solid rgba(255,255,255,0.08);
                    border-radius: 12px;
                    padding: 16px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    background: rgba(255,255,255,0.03);
                }
                .pm-tier-card:hover {
                    border-color: rgba(99,102,241,0.5);
                    background: rgba(99,102,241,0.08);
                }
                .pm-tier-card.loading-tier {
                    opacity: 0.65;
                    pointer-events: none;
                }

                .pm-tier-tag {
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    font-size: 10px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 3px 8px;
                    border-radius: 20px;
                    background: rgba(99,102,241,0.3);
                    color: #a5b4fc;
                    border: 1px solid rgba(99,102,241,0.4);
                }

                .pm-tier-name {
                    font-size: 14px;
                    font-weight: 600;
                    color: #fff;
                    margin-bottom: 4px;
                }

                .pm-tier-row {
                    display: flex;
                    align-items: baseline;
                    gap: 8px;
                }

                .pm-tier-price {
                    font-size: 22px;
                    font-weight: 800;
                    color: #fff;
                }

                .pm-tier-credits {
                    font-size: 12px;
                    color: rgba(255,255,255,0.45);
                }

                .pm-tier-per {
                    font-size: 11px;
                    color: rgba(99,102,241,0.8);
                    margin-top: 2px;
                }

                .pm-tier-spinner {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                    color: #a5b4fc;
                    margin-top: 6px;
                }

                .pm-spinner {
                    width: 14px;
                    height: 14px;
                    border: 2px solid rgba(99,102,241,0.3);
                    border-top-color: #6366f1;
                    border-radius: 50%;
                    animation: pm-spin 0.8s linear infinite;
                }

                @keyframes pm-spin {
                    to { transform: rotate(360deg); }
                }

                .pm-error-bar {
                    background: rgba(239,68,68,0.15);
                    border: 1px solid rgba(239,68,68,0.35);
                    color: #fca5a5;
                    font-size: 13px;
                    padding: 10px 14px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                @media (max-width: 480px) {
                    .pm-modal { max-width: 100%; border-radius: 14px; }
                }
            `}</style>

            <div className="pm-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
                <div className="pm-modal">
                    {/* Header */}
                    <div className="pm-header">
                        <div>
                            <div className="pm-header-title">Buy Credit Packs</div>
                            <div className="pm-header-sub">1 credit = 1 test run · Credits never expire</div>
                        </div>
                        <button className="pm-close-btn" onClick={handleClose}>✕</button>
                    </div>

                    <div className="pm-body">
                        {error && (
                            <div className="pm-error-bar">
                                ⚠ {error}
                            </div>
                        )}

                        {PRICING_TIERS.map(tier => (
                            <div
                                key={tier.id}
                                className={`pm-tier-card${loadingTier === tier.id ? ' loading-tier' : ''}`}
                                onClick={() => handleSelectTier(tier)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && handleSelectTier(tier)}
                            >
                                {tier.tag && <span className="pm-tier-tag">{tier.tag}</span>}
                                <div className="pm-tier-name">{tier.name}</div>
                                <div className="pm-tier-row">
                                    <span className="pm-tier-price">${tier.price}</span>
                                    <span className="pm-tier-credits">/ {tier.credits} runs</span>
                                </div>
                                <div className="pm-tier-per">
                                    ${(tier.price / tier.credits).toFixed(2)} per run
                                </div>
                                {loadingTier === tier.id && (
                                    <div className="pm-tier-spinner">
                                        <div className="pm-spinner" />
                                        Starting checkout…
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default PricingModal;
